import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  ActionItemDetailDto,
  ActionItemSummaryDto,
  EditOwnershipDto,
  FetchError,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
  UserDto,
} from "src/http";

type Variables = {
  actionItemGuid: string;
  owners: UserDto[];
  primaryOwner: UserDto | null;
  planGuid: string;
};

const useEditActionItemOwnersRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemOwnersRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditOwnershipDto = {
      ownerUserIds: variables.owners.map((x) => x.userId),
      primaryOwnerUserId: variables.primaryOwner?.userId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/owners`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemOwnersRequest };
};

const useEditActionItemOwners = () => {
  const { editActionItemOwnersRequest } = useEditActionItemOwnersRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemOwnersRequest,
    onMutate: async (variables) => {
      let cachedSummaryData: ActionItemSummaryDto | undefined;
      let cachedDetailData: ActionItemDetailDto | undefined;

      cachedSummaryData = await optimisticallyUpdateQueryData<ActionItemSummaryDto>(
        queryClient,
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        () => ({
          owners: variables.owners,
        })
      );

      cachedDetailData = await optimisticallyUpdateQueryData<ActionItemDetailDto>(
        queryClient,
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        () => ({
          owners: variables.owners,
          primaryOwner: variables.primaryOwner,
        })
      );

      return {
        cachedSummaryData,
        cachedDetailData,
      };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        context?.cachedSummaryData
      );

      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useEditActionItemOwners };
